import React from 'react';
import type { CatalogProperty } from 'src/api/generated/models/CatalogProperty';
import { PropertyPortal } from 'src/api/generated/models/PropertyPortal';
import { PropertyPortalPublicationConnectionState } from 'src/api/generated/models/PropertyPortalPublicationConnectionState';
import type { PropertyPortalPublication } from 'src/api/generated/models/PropertyPortalPublication';
import { Text } from 'src/modules/shared/components/atoms/text';
import { ProviderBrandAvatar } from 'src/modules/shared/components/molecules/provider-brand-avatar';
import type {
  ComponentAppearance,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
import {
  PiArrowSquareOutLight,
  PiCaretRight,
  PiGlobeHemisphereWestLight,
  PiGlobeXLight,
  PiPencilSimpleLineLight,
} from 'react-icons/pi';
import type { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  canPublishMeliPublication,
  canUnpublishMeliPublication,
  getSupportedMeliOperationFromPublication,
} from './meli-publication.util';
import { getPropertyPublicationProviderBrandId } from './property-publication-display.util';

export interface PropertyPublicationActionItem {
  appearance?: ComponentAppearance;
  key: string;
  kind: 'manage' | 'open_external' | 'publish' | 'unpublish';
  label: string;
  onClick: () => void;
  tooltip?: string;
  variant?: ComponentVariant;
}

export const propertyPublicationActionIconByKind: Record<
  PropertyPublicationActionItem['kind'],
  React.ReactNode
> = {
  manage: <PiPencilSimpleLineLight className="h-4 w-4 text-secondary-700" />,
  open_external: (
    <PiArrowSquareOutLight className="h-4 w-4 text-secondary-700" />
  ),
  publish: <PiGlobeHemisphereWestLight className="h-4 w-4 text-primary-700" />,
  unpublish: <PiGlobeXLight className="h-4 w-4 text-danger-700" />,
};

export const renderPropertyPublicationPortalAvatar = (
  portal: PropertyPortal
): React.ReactNode => {
  const providerBrandId = getPropertyPublicationProviderBrandId(portal);
  if (providerBrandId) {
    return <ProviderBrandAvatar providerId={providerBrandId} size="sm" />;
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500">
      <Text size="small" weight="medium">
        {String(portal).slice(0, 1).toUpperCase()}
      </Text>
    </div>
  );
};

export const buildPropertyPublicationActions = (params: {
  publication: PropertyPortalPublication;
  canManagePublications: boolean;
  canDeletePublications: boolean;
  isMeliConnected: boolean;
  isMeliSyncMode: boolean;
  onManagePublication: (publicationId: number) => void;
  t: ReturnType<typeof useI18n>['t'];
}): PropertyPublicationActionItem[] => {
  const actions: PropertyPublicationActionItem[] = [];
  const supportedMeliOperation =
    params.publication.portal === PropertyPortal.MELI
      ? getSupportedMeliOperationFromPublication(params.publication.operation)
      : null;
  const canManageThisPublication =
    params.canManagePublications &&
    params.publication.portal === PropertyPortal.MELI &&
    supportedMeliOperation != null &&
    params.isMeliConnected;

  if (canManageThisPublication) {
    actions.push({
      key: 'manage',
      kind: 'manage',
      label: params.t('catalog.properties.editor.publishing.actions.manage'),
      tooltip: params.t(
        'catalog.properties.editor.publishing.actions.manageTooltip'
      ),
      onClick: () => params.onManagePublication(params.publication.id),
    });
  }

  if (
    canManageThisPublication &&
    !params.isMeliSyncMode &&
    canPublishMeliPublication(params.publication)
  ) {
    actions.push({
      key: 'publish',
      kind: 'publish',
      label: params.t('catalog.properties.editor.publishing.actions.publish'),
      tooltip: params.t(
        'catalog.properties.editor.publishing.actions.publishTooltip'
      ),
      onClick: () => params.onManagePublication(params.publication.id),
      variant: 'primary',
      appearance: 'soft',
    });
  } else if (
    params.publication.portal === PropertyPortal.MELI &&
    supportedMeliOperation != null &&
    params.canDeletePublications &&
    !params.isMeliSyncMode &&
    canUnpublishMeliPublication(params.publication)
  ) {
    actions.push({
      key: 'unpublish',
      kind: 'unpublish',
      label: params.t('catalog.properties.editor.publishing.actions.unpublish'),
      tooltip: params.t(
        'catalog.properties.editor.publishing.actions.unpublishTooltip'
      ),
      onClick: () => params.onManagePublication(params.publication.id),
      variant: 'danger',
      appearance: 'soft',
    });
  }

  return actions;
};

export const renderPropertyPublicationExternalCell = (params: {
  publication: PropertyPortalPublication;
  canReadPublicationsForActions: boolean;
  t: ReturnType<typeof useI18n>['t'];
}): React.ReactNode => {
  const publicationCode = params.publication.externalListingId?.trim() ?? '';

  if (
    !params.canReadPublicationsForActions ||
    !params.publication.externalUrl ||
    publicationCode.length === 0
  ) {
    if (
      params.publication.connectionState ===
      PropertyPortalPublicationConnectionState.REFERENCE_ONLY
    ) {
      return null;
    }

    return (
      <Text size="small" className="text-slate-400">
        —
      </Text>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        window.open(
          params.publication.externalUrl!,
          '_blank',
          'noopener,noreferrer'
        );
      }}
      className="inline-flex items-center gap-2 text-left text-slate-700 transition hover:text-slate-900"
      aria-label={params.t(
        'catalog.properties.editor.publishing.actions.openExternal'
      )}
    >
      <Text size="small" weight="medium" className="text-inherit">
        {publicationCode}
      </Text>
      <PiArrowSquareOutLight className="h-4 w-4 text-inherit" />
    </button>
  );
};

export const renderPropertyPublicationPropertyCell = (params: {
  publication: PropertyPortalPublication;
  propertyById: Map<number, CatalogProperty | null>;
  canReadProperties: boolean;
  t: ReturnType<typeof useI18n>['t'];
  onOpenProperty: (propertyId: number) => void;
}): React.ReactNode => {
  const property =
    params.propertyById.get(params.publication.propertyId) ?? null;
  const propertyName =
    property?.name.trim() ??
    params.t('catalog.properties.publications.property.unknownName');
  const propertyAddress =
    property?.addressDisplay?.trim() ??
    params.t('catalog.properties.publications.property.unknownAddress');

  const content = (
    <>
      <div className="min-w-0 flex-1 py-1">
        <Text
          size="small"
          weight="medium"
          className="break-words text-slate-900"
        >
          {propertyName}
        </Text>
        <Text size="small" className="break-words text-slate-500">
          {propertyAddress}
        </Text>
      </div>
      <div className="flex items-center py-1 text-slate-400">
        <PiCaretRight className="h-4 w-4" />
      </div>
    </>
  );

  if (!params.canReadProperties) {
    return <div className="flex min-w-0 items-stretch gap-3">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => params.onOpenProperty(params.publication.propertyId)}
      className="flex min-w-0 items-stretch gap-3 text-left"
    >
      {content}
    </button>
  );
};
